import React, { useEffect, useState } from "react";
import "./Header.scss";
import { useLocation } from "react-router-dom";
import {
  Button,
  Dropdown,
  MenuProps,
  Avatar,
  Drawer,
  Badge,
  InputNumber,
  Empty,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "contexts/UserContext";
import {
  BellOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  LogoutOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Helpers from "@utils/Helpers";
import { useCart } from "contexts/CartContext";
import { Image } from "antd";
import { useUnit } from "contexts/UnitContext";
import { usePusher } from "contexts/PusherContext";
import NotiItem from "@components/NotiItem/NotiItem";
import { notificationApi } from "services/NotificationApi";
import { errorLog } from "logging";
import contactIcon from "@assets/icons/contact.svg";

const Header: React.FC = () => {
  const { units, updateUnit } = useUnit();
  const [cartItems, setCartItems] = useState<MenuProps["items"]>([]);
  const [notiItems, setNotiItems] = useState<MenuProps["items"]>([]);
  const [notiCount, setNotiCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpenCartDropdown, setIsOpenCartDropdown] = useState(false);
  const [isOpenNotiDropdown, setIsOpenNotiDropdown] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { logOut, isAuthenticated } = useAuth();
  const { userCart } = useCart();
  const { message } = usePusher();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userProfile,
    messageCount,
    playNoti,
    setIsOpenChatbox,
    isOpenChatbox,
    setFetchCount,
  } = useUser();

  const profileItems: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Link to="/profile?direct=personal-info" className="user-menu-item">
          <UserOutlined />
          <span>Profile</span>
        </Link>
      ),
    },
    {
      key: "2",
      label: (
        <Link to="/profile?direct=payment" className="user-menu-item">
          <CreditCardOutlined />
          <span>Payment Methods</span>
        </Link>
      ),
    },
    {
      type: "divider",
    },
    {
      key: "3",
      label: (
        <Link to="/profile?direct=setting" className="user-menu-item">
          <SettingOutlined />
          <span>Settings</span>
        </Link>
      ),
    },
    {
      key: "4",
      danger: true,
      onClick: () => {
        logOut();
        navigate("/auth");
      },
      label: (
        <div className="user-menu-item last">
          <LogoutOutlined />
          <span>Logout</span>
        </div>
      ),
    },
  ];

  const handleSelectUnit = (key: string, value: any) => {
    updateUnit({ ...units, [key]: value });
  };

  // BIND TRANSACTION PUSHER
  useEffect(() => {
    if (message && message.module !== "Message") {
      const item = {
        key: (notiItems!.length + 1).toString(),
        label: <NotiItem type={message.module} message={message.message} />,
      };
      playNoti();
      setNotiCount((prev) => prev + 1);
      setNotiItems((prevItems) => [item, ...prevItems!]);
    }
  }, [message]);

  // SUMMARY CART
  useEffect(() => {
    const totalCarts: MenuProps["items"] = [];
    if (userCart.length > 0) {
      userCart.forEach((item: any, index: number) => {
        const cartItem = {
          key: item.product_id,
          label: <CartItemDropdown data={item} />,
        };
        totalCarts.push(cartItem);
      });
    } else {
      const cartItem = {
        key: 1,
        label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />,
      };
      totalCarts.push(cartItem);
    }
    setCartItems(totalCarts);
  }, [userCart]);

  // HANDLE SCROLL & FETCH NOTIFICATION
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    if (isAuthenticated) {
      fetchNotification();
      setFetchCount(true);
    }
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // FETCH NOTIFICATION
  const fetchNotification = async () => {
    try {
      const response = await notificationApi.getNotification();
      if (response.status === 200) {
        let viewedCount = 0;
        response.data.data.notifications.map((item: any, index: number) => {
          if (!item.is_viewed) {
            viewedCount += 1;
          }
          const type = item.module;
          const noti = {
            key: (index + 1).toString(),
            label: <NotiItem type={type} notification={item} />,
          };
          setNotiItems((prevItems) => [noti, ...prevItems!]);
        });
        setNotiCount(viewedCount);
      }
    } catch (error) {
      Helpers.notification.error("Can not get notification");
      errorLog("Header.tsx - fetchNotification", error);
    }
  };

  // HANDLE VIEWED NOTIFICATION
  const handleUpdateViewed = async () => {
    try {
      const response = await notificationApi.updateViewAll();
      if (response.status === 200) {
        setNotiCount(0);
      }
    } catch (error) {
      Helpers.notification.error("Can not update view all");
      errorLog("Header.tsx - handleUpdateViewed", error);
    }
  };

  return (
    <nav
      className={`navbar navbar-expand-lg Header p-0 ${
        isScrolled || location.pathname !== "/home" ? "scrolled" : ""
      }`}
    >
      <div className="container py-0">
        <Link className="navbar-brand" to="/home">
          <div className="d-flex align-items-center gap-2">
            <img src={Helpers.getImageFrontPage("logo")} height="70px" alt="" />
          </div>
        </Link>
        <button
          className="navbar-toggler text-dark"
          onClick={() => setOpenDrawer(true)}
        >
          <UnorderedListOutlined />
        </button>
        <Drawer onClose={() => setOpenDrawer(false)} open={openDrawer}>
          <Link className="slide-item" to="/products">
            Products
          </Link>
          <Link className="slide-item" to="/producer">
            Producers
          </Link>
          {!isAuthenticated ? (
            <Button
              className="slide-item login-btn"
              onClick={() => {
                navigate("/auth");
              }}
            >
              Login
            </Button>
          ) : (
            <div className="d-flex justify-content-center gap-3 align-items-center">
              <Button className="notification-btn" icon={<BellOutlined />} />
              <Badge count={userCart.length}>
                <Button
                  className="cart-btn"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => navigate("/cart")}
                />
              </Badge>
              <Dropdown menu={{ items: profileItems }}>
                {userProfile?.avatar ? (
                  <img
                    className="user-avatar"
                    width={32}
                    height={32}
                    src={userProfile?.avatar!}
                    alt="avatar"
                  />
                ) : (
                  <Avatar size={32} className="user-avatar default">
                    {userProfile?.first_name[0].toUpperCase()}
                  </Avatar>
                )}
              </Dropdown>
            </div>
          )}
        </Drawer>
        <div
          className="collapse navbar-collapse justify-content-end"
          id="navbarSupportedContent"
        >
          {/* <Switch
            checked={isDeveloperMode}
            onChange={(e) => setIsDeveloperMode(e)}
            checkedChildren={`Dev`}
          /> */}
          <div className="d-flex justify-content-end menu-group">
            <ul className="navbar-nav mb-2 mb-lg-0 text-center gap-5 header-menu w-75 justify-content-end align-items-center">
              <li className="nav-item"></li>
              <li
                className={`nav-item ${
                  window.location.pathname === "/products" ? "active" : ""
                }`}
              >
                <Link className="nav-link " to={"/products"}>
                  Products
                </Link>
              </li>
              <li
                className={`nav-item ${
                  window.location.pathname.includes("producer") ? "active" : ""
                }`}
              >
                <Link className="nav-link" to={"/producer"}>
                  Producers
                </Link>
              </li>
              <li
                className={`nav-item ${
                  window.location.pathname.includes("transaction")
                    ? "active"
                    : ""
                }`}
              >
                <Link className="nav-link" to={"/transaction"}>
                  Transactions
                </Link>
              </li>

              {!isAuthenticated ? (
                <Button
                  className="login-btn"
                  onClick={() => {
                    navigate("/auth");
                  }}
                >
                  Login
                </Button>
              ) : (
                <div className="personal-group">
                  <Badge count={messageCount}>
                    <Button
                      onClick={() => setIsOpenChatbox(!isOpenChatbox)}
                      className="message-btn"
                      icon={<img src={contactIcon} alt="contact" width={20} />}
                    />
                  </Badge>
                  <Dropdown
                    menu={{ items: notiItems }}
                    trigger={["click"]}
                    placement="bottomRight"
                    open={isOpenNotiDropdown}
                    overlayClassName="noti-dropdown"
                    overlayStyle={{ width: 300 }}
                    onOpenChange={(isOpen) => {
                      handleUpdateViewed();
                      setIsOpenNotiDropdown(isOpen);
                    }}
                  >
                    <Badge count={notiCount}>
                      <Button
                        className="notification-btn"
                        icon={<BellOutlined />}
                      />
                    </Badge>
                  </Dropdown>
                  <Dropdown
                    menu={{ items: cartItems }}
                    trigger={["click"]}
                    open={isOpenCartDropdown}
                    onOpenChange={(isOpen) => setIsOpenCartDropdown(isOpen)}
                    overlayClassName="cart-dropdown"
                    placement="bottomRight"
                    dropdownRender={(menu) => (
                      <div onClick={(e) => e.stopPropagation()}>
                        {React.cloneElement(
                          menu as React.ReactElement<{
                            style: React.CSSProperties;
                          }>
                        )}
                        <div className="view-cart-dropdown">
                          <Button
                            type="link"
                            icon={<ShoppingCartOutlined />}
                            className="view-cart-btn"
                            onClick={() => {
                              setIsOpenCartDropdown(false);
                              navigate("/cart");
                            }}
                          >
                            View cart
                          </Button>
                        </div>
                      </div>
                    )}
                  >
                    <Badge count={userCart.length}>
                      <Button
                        className="cart-btn"
                        icon={<ShoppingCartOutlined />}
                      />
                    </Badge>
                  </Dropdown>
                  <Dropdown menu={{ items: profileItems }}>
                    {userProfile?.avatar ? (
                      <img
                        className="user-avatar"
                        width={32}
                        height={32}
                        src={userProfile?.avatar!}
                        alt="avatar"
                      />
                    ) : (
                      <Avatar size={32} className="user-avatar default">
                        {userProfile?.first_name[0].toUpperCase()}
                      </Avatar>
                    )}
                  </Dropdown>
                </div>
              )}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

let updateTimer: any;

const CartItemDropdown: React.FC<any> = ({ data }) => {
  const { units, convertCurrency } = useUnit();
  const [isRemove, setIsRemove] = useState(false);
  const { removeItem, updateQuantity, userCart, setUserCart } = useCart();
  const convertPrice = convertCurrency(
    data.discounted_price
      ? data.discounted_price
      : data.unit == "Ton"
      ? data.price_per_tons
      : data.price_per_bale,
    "USD",
    units.currency
  );
  const handleRemoveItem = () => {
    setIsRemove(true);
    const payload = {
      product_id: data.product_id,
    };
    removeItem(payload);
  };

  const handleUpdateQuantity = (value: number) => {
    if (value == 0) {
      value = 1;
    }
    if (value > data.total_quantity || value < 1) {
      return;
    }

    const updatedItems = userCart.map((ite) =>
      ite.product_id === data.product_id ? { ...ite, quantity: value } : ite
    );
    setUserCart(updatedItems);

    const payload = {
      product_id: data.product_id,
      quantity: value,
    };

    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      updateQuantity(payload);
    }, 500);
  };

  return (
    <div
      className={`CartItemDropdown CartItem-${data.product_id} ${
        isRemove ? "removing" : ""
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cart-item-wrap d-flex gap-2 align-items-center">
        <div className="flex-shrink-0">
          <Image
            className="cart-item-image"
            width={70}
            height={50}
            src={data.image}
          />
        </div>
        <div className="flex-grow-1">
          <div className="item-name">{data.hay_type}</div>
          <div className="item-price">
            ${Helpers.formatNumber(convertPrice, 2)} / {data.unit}
          </div>
        </div>
        <div className="quantity-control">
          <InputNumber
            variant="borderless"
            value={data.quantity}
            max={data.total_quantity}
            className="quantity-input"
            onChange={(value) => handleUpdateQuantity(value)}
          />
        </div>
        <Button
          danger
          type="link"
          size="small"
          icon={<DeleteOutlined />}
          onClick={handleRemoveItem}
        />
      </div>
    </div>
  );
};

export default Header;
